import { DocumentServicesObject, PageRef } from '@wix/document-services-types';
import { ErrorReporter } from '@wix/editor-error-reporter';
import { IntentInstallOptions } from '@wix/editor-platform-host-integration-apis';
import {
  PlatformApiMethods,
  PlatformContext,
} from '../../../../types/platformApi';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';
import { createDependenciesDriver } from '../../appDependenciesDriver';
import { IntentInstallStore } from '../IntentInstallStore';
import type { ProgressBarManager } from '../ProgressBarManager';
import {
  AFTER_INSTALL_APP_HISTORY_LABEL,
  BEFORE_INSTALL_APP_HISTORY_LABEL,
} from '../constants';
import { createIntentInstallError, IntentInstallErrorCode } from '../errors';
import { BaseInstallationStrategy } from './BaseInstallationStrategy';
import { InstallationStrategy } from './types';

export const PENDING_APPS_SERIAL_INSTALL_V2 = 'se_pendingAppsSerialInstallV2';

export class SerialInstallationStrategyV2 implements InstallationStrategy {
  private baseInstallStrategy: BaseInstallationStrategy;

  constructor(
    private platformApiMethods: PlatformApiMethods,
    private documentServicesAPI: DocumentServicesObject,
    private experimentsAPI: IExperimentsApiAdapter,
    private intentInstallStore: IntentInstallStore,
    private progressBarManager: ProgressBarManager,
  ) {
    this.baseInstallStrategy = new BaseInstallationStrategy(
      this.platformApiMethods,
      this.documentServicesAPI,
      this.experimentsAPI,
      this.intentInstallStore,
      this.progressBarManager,
    );
  }

  private async navigateToApp(appDefinitionId: string) {
    const allPages = this.documentServicesAPI.pages.getPagesData();
    const pageId = allPages.find((page) => {
      const isPageVisible = !page.hidePage;
      const isPageBelongsToCurrentApp =
        page.appDefinitionId === appDefinitionId;
      return isPageBelongsToCurrentApp && isPageVisible;
    })?.id;
    if (!pageId) {
      return;
    }
    const pageRef = this.documentServicesAPI.pages.getReference(
      pageId,
    ) as PageRef;

    if (!pageRef) {
      return;
    }

    const appData =
      this.documentServicesAPI.platform.getAppDataByAppDefId(appDefinitionId);

    await this.platformApiMethods.document.pages.navigateTo(appData, '', {
      pageRef,
    });
  }

  public async install(
    context: PlatformContext,
    apps: string[],
    options: IntentInstallOptions,
  ): Promise<void> {
    for (const appDefId of apps) {
      await context.platformApiMethods.document.history.add({}, '', {
        label: BEFORE_INSTALL_APP_HISTORY_LABEL + appDefId,
      });

      try {
        const { dependenciesToInstall, dependencyOptions } =
          await this.getAppDependencies(appDefId, apps);

        const appsOptionsWithDependencies = {
          ...(dependencyOptions || {}),
          ...(options.appsOptions || {}),
        };

        await this.baseInstallStrategy.install(
          context,
          [...dependenciesToInstall, appDefId],
          {
            ...options,
            appsOptions: appsOptionsWithDependencies,
          },
          {
            shouldUpdateProgressBar: true,
          },
        );
        await context.platformApiMethods.document.history.add({}, '', {
          label: AFTER_INSTALL_APP_HISTORY_LABEL + appDefId,
        });
      } catch (err) {
        ErrorReporter.captureException(err, {
          tags: { intentInstall: true },
        });
        console.error(err);
        this.intentInstallStore.addAppWithError(appDefId);
        await context.platformApiMethods.document.history.performUndoUntilLabel(
          {},
          '',
          { snapshotLabel: BEFORE_INSTALL_APP_HISTORY_LABEL + appDefId },
        );
      }
    }

    if (!this.intentInstallStore.isSilent() && apps.length > 0) {
      const lastApp = apps[apps.length - 1];
      this.navigateToApp(lastApp).catch((err) => {
        console.error(
          createIntentInstallError(IntentInstallErrorCode.FailedToNavigateToApp)
            .withParentError(err)
            .withAppDefinitionId(lastApp),
        );
      });
    }
  }

  private async getAppDependencies(appDefId: string, appDefIds: string[]) {
    const dependenciesDriver = await createDependenciesDriver(
      this.documentServicesAPI,
      appDefId,
    );

    const { appsToInstall, dependencyOptions } =
      dependenciesDriver.getAppsToInstall();

    const dependenciesToInstall = appsToInstall.filter(
      (dependencyAppDefId) =>
        dependencyAppDefId !== appDefId &&
        !appDefIds.includes(dependencyAppDefId),
    );

    return { dependenciesToInstall, dependencyOptions };
  }
}
